import React, { useEffect, useState, useMemo } from "react";
import {
    Box, Typography, Paper, Grid, TextField, Checkbox, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Divider, Card, Stack, CircularProgress, Button,
    FormControl, InputLabel, Select, ToggleButton, ToggleButtonGroup,
    ListItemText, OutlinedInput,
    Chip,
    Avatar
} from "@mui/material";
import { Delete, Person, School, Save, Add, Settings } from "@mui/icons-material";
import { collection, getDocs, query, where, doc, getDoc, writeBatch, Timestamp, orderBy } from "firebase/firestore";
import { db, auth } from "../../firebase";
import Swal from "sweetalert2";
import { Autocomplete } from "@mui/material";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

function FeeStructureSetup() {
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState("single");
    const [schoolId, setSchoolId] = useState(null);
    const [students, setStudents] = useState([]);
    const [discountTypes, setDiscountTypes] = useState([]);
    const [courses, setCourses] = useState([]);

    const [selectedClass, setSelectedClass] = useState("");
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [selectedStudentIds, setSelectedStudentIds] = useState([]);
    const [feeHeads, setFeeHeads] = useState([]);
    const [appliedDiscounts, setAppliedDiscounts] = useState([{ typeId: "", amount: 0 }]);

    const [installmentCount, setInstallmentCount] = useState(1);
    const [manualInstallments, setManualInstallments] = useState([]);
    const [feeHeadMaster, setFeeHeadMaster] = useState([]); // All available heads
    const [customHead, setCustomHead] = useState({ headId: "", name: "", amount: "" });
    const [feeHeadInstallments, setFeeHeadInstallments] = useState({});
    const [isFetchedData, setIsFetchedData] = useState(false);
    const [selectedType, setSelectedType] = useState("");
    const [feeHeadMap, setFeeHeadMap] = useState({});
    const installmentHeads = feeHeads.filter(h => h.checked && h.installment === "Yes");
    const normalHeads = feeHeads.filter(h => h.checked && h.installment !== "Yes");
    const subTotal = normalHeads.reduce((sum, h) => sum + (parseFloat(h.amount) || 0), 0);
    const installmentHeadsTotal = installmentHeads.reduce((sum, h) => sum + (parseFloat(h.amount) || 0), 0);
    const totalDiscount = appliedDiscounts.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);

    useEffect(() => {
        setFeeHeadInstallments(prev => {
            let updated = { ...prev };

            feeHeads.forEach(h => {
                if (h.installment === "Yes" && h.checked) {
                    if (!updated[h.headId]) {
                        updated[h.headId] = [
                            {
                                name: `${h.name} - Inst 1`,
                                amount: h.amount || 0,
                                dueDate: ""
                            }
                        ];
                    }
                }
            });

            return updated;
        });
    }, [feeHeads]);

    const addInstallmentRow = (headId) => {
        setFeeHeadInstallments(prev => ({
            ...prev,
            [headId]: [
                ...(prev[headId] || []),
                { name: "Installment", amount: 0, dueDate: "" }
            ]
        }));
    };

    const handleHeadInstallmentChange = (headId, index, field, value) => {
        const updated = { ...feeHeadInstallments };

        updated[headId][index] = {
            ...updated[headId][index],
            [field]: field === "amount" ? parseFloat(value) || 0 : value
        };

        setFeeHeadInstallments(updated);
    };

    useEffect(() => {
        if (mode === "bulk" && selectedClass) {
            const filtered = students.filter(s => {
                const classMatch = s.class === selectedClass;

                const typeMatch =
                    !selectedType ||
                    (s.studenttype || "").toLowerCase().includes(selectedType.toLowerCase());

                return classMatch && typeMatch;
            });

            setSelectedStudentIds(filtered.map(s => s.id));
        }
    }, [selectedType, selectedClass]);

    useEffect(() => { fetchInitialData(); }, []);

    const fetchInitialData = async () => {
        setLoading(true);

        try {
            // ✅ 1. Get user schoolId
            const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
            const sId = userSnap.data().schoolId;
            setSchoolId(sId);

            // ✅ 2. Prepare queries
            const courseQuery = query(
                collection(db, "courses"),
                where("schoolId", "==", sId),
                orderBy("sequence", "asc")
            );

            const feeHeadQuery = query(
                collection(db, "feeheads"),
                where("schoolId", "==", sId),
                orderBy("sequence_no", "asc")
            );

            // ✅ 3. Parallel fetch
            const [admSnap, studentSnap, courseSnap, discSnap, headsSnap] = await Promise.all([
                getDocs(query(collection(db, "admission"), where("schoolId", "==", sId), where("status", "==", "Active"))),
                getDocs(query(collection(db, "students"), where("schoolId", "==", sId))),
                getDocs(courseQuery),
                getDocs(query(collection(db, "discountTypes"), where("schoolId", "==", sId))),
                getDocs(feeHeadQuery) // ✅ FIX: fetch here
            ]);

            // =========================
            // ✅ Students Mapping
            // =========================
            const studentMap = {};
            studentSnap.docs.forEach(d => {
                studentMap[d.id] = d.data();
            });

            // =========================
            // ✅ Courses
            // =========================
            const courseList = courseSnap.docs.map(d => ({
                id: d.id,
                ...d.data()
            }));
            setCourses(courseList);

            const courseMap = {};
            courseList.forEach(c => {
                courseMap[c.id] = c.name;
            });

            // =========================
            // ✅ Discount Types
            // =========================
            setDiscountTypes(
                discSnap.docs.map(d => ({
                    id: d.id,
                    ...d.data()
                }))
            );

            // =========================
            // ✅ Merge Admission + Student
            // =========================
            const merged = admSnap.docs.map(doc => {
                const adm = { id: doc.id, ...doc.data() };
                const sData = studentMap[adm.studentId] || {};

                return {
                    ...adm,
                    fullName: `${sData.firstname || ''} ${sData.fathername || ''} ${sData.surname || ''}`
                        .trim()
                        .toUpperCase(),
                    className: courseMap[adm.class] || "N/A",
                    studentPhoto: sData.photoURL,
                };
            });

            setStudents(merged);

            // =========================
            // ✅ Fee Heads (MASTER)
            // =========================
            const heads = headsSnap.docs.map(d => ({
                id: d.id,
                fee_heads: d.data().fee_heads,
                defaultAmount: d.data().amount || 0,
                installment: d.data().installment || "No",
                sequence_no: d.data().sequence_no || 0
            }));

            setFeeHeadMaster(heads);

            // ✅ Create fast lookup map
            const map = {};
            heads.forEach(h => {
                map[h.id] = h;
            });

            setFeeHeadMap(map);

        } catch (e) {
            console.error("Fetch Error:", e);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectionChange = async (val, type) => {
        if (!val) return;
        setFeeHeads([]);
        if (type === 'student') {
            setSelectedStudent(val);
            const exists = await fetchExistingStructure(val.id);

            if (!exists) {
                await fetchClassHeads(val.class);
            }
        } else {
            setSelectedClass(val);
            // In Bulk Mode, filter students of this class and select all by default
            const classStudents = students.filter(s => s.class === val);
            setSelectedStudentIds(classStudents.map(s => s.id));

            const exists = await fetchExistingStructure(val, true);
            if (!exists) fetchClassHeads(val);
        }
    };

    const fetchExistingStructure = async (id, isClass = false) => {
        const q = query(collection(db, "fee_structures"), where(isClass ? "classId" : "admissionId", "==", id));
        const snap = await getDocs(q);
        if (!snap.empty) {
            const data = snap.docs[0].data();

            // 1. Sort and set Fee Heads
            const sortedHeads = (data.heads || []).sort((a, b) =>
                (Number(a.sequence_no) || 0) - (Number(b.sequence_no) || 0)
            );
            setFeeHeads(sortedHeads);

            // 2. Set Discounts
            setAppliedDiscounts(data.discounts || [{ typeId: "", amount: 0 }]);

            // 3. IMPORTANT: Set existing installments and count
            // ✅ LOAD NORMAL INSTALLMENTS
            if (data.normalInstallments && data.normalInstallments.length > 0) {
                setManualInstallments(data.normalInstallments);

                const autoInst = data.normalInstallments.filter(i => i.type === "auto");
                setInstallmentCount(autoInst.length || 1);
            }

            // ✅ LOAD HEAD-WISE INSTALLMENTS
            if (data.headWiseInstallments) {
                setFeeHeadInstallments(data.headWiseInstallments);
            }

            return true;
        }
        return false;
    };


    const fetchClassHeads = async (classId) => {
        const q = query(
            collection(db, "feeHeadAssignments"),
            where("classId", "==", classId),
            orderBy("sequence_no", "asc")
        );

        const snap = await getDocs(q);

        let heads = snap.docs.map(d => {   // ✅ rename to 'd' to avoid conflict
            const data = d.data();

            const master = feeHeadMap[data.feeHeadId]; // ✅ correct usage

            return {
                headId: data.feeHeadId,
                name: data.feeHeadName,
                amount: data.amount,
                sequence_no: data.sequence_no || 0,
                installment: master?.installment || "No",
                checked: true
            };
        });

        // ✅ Transport logic
        if (mode === "single" && selectedStudent) {
            const transportHeadIndex = heads.findIndex(
                h => h.headId === "9da5fe5b-606e-49d1-89cc-173698c38dbf"
            );

            if (transportHeadIndex !== -1) {
                const fee = await fetchTransportFee(selectedStudent.id);
                heads[transportHeadIndex].amount = fee;
            }
        }

        setFeeHeads(heads);
    };

    const handleInstallmentChange = (index, field, value) => {
        const updated = [...manualInstallments];
        updated[index] = {
            ...updated[index],
            [field]: field === 'amount' ? (parseFloat(value) || 0) : value
        };
        setManualInstallments(updated);
    };

    useEffect(() => {

        if (isFetchedData) return; // 🚀 STOP override after fetch

        let newInst = [];

        if (subTotal > 0) {
            const perInst = Math.floor(subTotal / installmentCount);
            const remainder = subTotal - perInst * installmentCount;

            newInst = Array.from({ length: installmentCount }, (_, i) => ({
                name: `Installment ${i + 1}`,
                amount: i === installmentCount - 1 ? perInst + remainder : perInst,
                dueDate: "",
                type: "auto"
            }));
        }

        Object.entries(feeHeadInstallments).forEach(([headId, arr]) => {
            arr.forEach(inst => {
                newInst.push({
                    name: inst.name || "",
                    amount: parseFloat(inst.amount) || 0,
                    dueDate: inst.dueDate || "",
                    type: "manual",
                    headId
                });
            });
        });

        setManualInstallments(newInst);

    }, [subTotal, installmentCount, feeHeadInstallments, isFetchedData]);

    useEffect(() => {
        setManualInstallments(prev => prev.map(inst =>
            inst.isLast ? { ...inst, applicableDiscount: totalDiscount } : inst
        ));
    }, [totalDiscount]);

    const handleBulkSave = async () => {

        const expectedTotal =
            subTotal + // normal heads
            Object.values(feeHeadInstallments).flat().reduce(
                (sum, i) => sum + (parseFloat(i.amount) || 0), 0
            );

        const totalInstPlanned = manualInstallments.reduce(
            (sum, i) => sum + (parseFloat(i.amount) || 0), 0
        );

        if (Math.round(totalInstPlanned) !== Math.round(expectedTotal)) {
            return Swal.fire(
                "Error",
                `Total Installments (₹${totalInstPlanned}) must match Total (₹${expectedTotal})`,
                "error"
            );
        }

        let targetIds = [];

        if (mode === 'single') {
            targetIds = [selectedStudent?.id];
        } else {
            targetIds = selectedStudentIds.filter(id => {
                const student = students.find(s => s.id === id);

                if (!student) return false;

                // ✅ Apply strict type filter
                if (selectedType === "Boarding") {
                    return (student.studenttype || "").toLowerCase().includes("boarding");
                }

                if (selectedType === "Day School") {
                    return (student.studenttype || "").toLowerCase().includes("day");
                }

                return true; // All if no filter
            });
        }
        const validIds = targetIds.filter(Boolean);

        if (validIds.length === 0) {
            return Swal.fire("Warning", "Select at least one Student", "warning");
        }

        setLoading(true);

        try {
            const batch = writeBatch(db);

            validIds.forEach(id => {

                const ref = doc(collection(db, "fee_structures"), id);

                // ✅ CLEAN HEADS
                const cleanHeads = feeHeads.map(h => ({
                    headId: h.headId || "",
                    name: h.name || "",
                    amount: parseFloat(h.amount) || 0,
                    checked: h.checked ?? true,
                    installment: h.installment || "No",
                    sequence_no: h.sequence_no || 0,
                }));

                // ✅ CLEAN DISCOUNTS
                const cleanDiscounts = (appliedDiscounts || []).map(d => ({
                    typeId: d.typeId || "",
                    amount: parseFloat(d.amount) || 0,
                }));

                // ✅ CLEAN NORMAL INSTALLMENTS
                const cleanManualInstallments = (manualInstallments || []).map(i => ({
                    name: i.name || "",
                    amount: parseFloat(i.amount) || 0,
                    dueDate: i.dueDate || "",
                    type: i.type || "",
                    headId: i.headId || "",
                }));

                // ✅ CLEAN HEAD-WISE INSTALLMENTS
                const cleanHeadInstallments = {};

                Object.keys(feeHeadInstallments || {}).forEach(headId => {
                    cleanHeadInstallments[headId] =
                        (feeHeadInstallments[headId] || []).map(inst => ({
                            name: inst.name || "",
                            amount: parseFloat(inst.amount) || 0,
                            dueDate: inst.dueDate || ""
                        }));
                });

                batch.set(ref, {
                    schoolId: schoolId || "",
                    classId: mode === 'bulk'
                        ? (selectedClass || "")
                        : (selectedStudent?.class || ""),

                    admissionId: id,

                    heads: cleanHeads,
                    discounts: cleanDiscounts,

                    subTotal: subTotal || 0,
                    totalDiscount: totalDiscount || 0,
                    totalAmount: totalAmount || 0,

                    normalInstallments: cleanManualInstallments,
                    headWiseInstallments: cleanHeadInstallments,

                    updatedAt: Timestamp.now(),
                    status: "Pending"

                }, { merge: true });

            });

            await batch.commit();

            Swal.fire(
                "Success",
                `Saved for ${validIds.length} students`,
                "success"
            );

            resetForm();

        } catch (error) {
            console.error("Save Error:", error);
            Swal.fire("Error", error.message, "error");
        } finally {
            setLoading(false);
        }
    };
    const studentsInClass = useMemo(() => {
        return students.filter(s => {
            const classMatch = s.class === selectedClass;

            // 👇 filter by student type
            const typeMatch =
                !selectedType || // All
                (s.studenttype || "").toLowerCase().includes(selectedType.toLowerCase());

            return classMatch && typeMatch;
        });
    }, [students, selectedClass, selectedType]);

    if (loading) return <Box display="flex" justifyContent="center" alignItems="center" height="80vh"><CircularProgress /></Box>;

    const addCustomHead = async () => {

        if (!customHead.headId) {
            return Swal.fire("Error", "Please select a head", "error");
        }

        const amount = parseFloat(customHead.amount);

        // ✅ Prevent adding if amount is empty or 0
        if (!amount || amount <= 0) {
            return Swal.fire("Error", "Please enter valid amount", "error");
        }

        // ✅ Prevent duplicate
        if (feeHeads.some(h => h.headId === customHead.headId)) {
            return Swal.fire("Note", "This fee head is already added", "info");
        }

        const masterRecord = feeHeadMaster.find(m => m.id === customHead.headId);

        const newHead = {
            headId: customHead.headId,
            name: customHead.name,
            amount: amount, // ✅ USE ENTERED AMOUNT
            checked: true,
            isCustom: true,
            installment: masterRecord?.installment || "No",
            sequence_no: masterRecord?.sequence_no || 99
        };

        setFeeHeads(prev => [...prev, newHead]);

        // ✅ Reset input after add
        setCustomHead({ headId: "", name: "", amount: "" });
    };

    const resetForm = () => {
        setSelectedStudent(null);
        setSelectedClass("");
        setSelectedStudentIds([]);
        setFeeHeads([]);
        setAppliedDiscounts([{ typeId: "", amount: 0 }]);
        setInstallmentCount(1);
        setManualInstallments([]);
    };

    const totalAmount =
        subTotal +
        Object.values(feeHeadInstallments).flat().reduce(
            (sum, i) => sum + (parseFloat(i.amount) || 0), 0
        ) -
        totalDiscount;

    const removeAllInstallments = (headId) => {
        setFeeHeadInstallments(prev => {
            const updated = { ...prev };
            delete updated[headId];
            return updated;
        });

        setManualInstallments(prev =>
            prev.filter(inst => inst.headId !== headId)
        );
    };

    const removeInstallmentRow = (headId, index) => {

        setFeeHeadInstallments(prev => {
            const updated = { ...prev };

            if (updated[headId].length <= 1) {
                return prev; // don’t allow deleting last row
            }

            if (!updated[headId]) return prev;

            updated[headId] = updated[headId].filter((_, i) => i !== index);

            if (updated[headId].length === 0) {
                delete updated[headId];
            }

            return updated;
        });
    };

    const fetchTransportFee = async (studentId) => {
        try {
            const admRef = doc(db, "admission", studentId);
            const admSnap = await getDoc(admRef);
            if (!admSnap.exists()) return 0;
            const transportLocationId = admSnap.data().transport;
            if (!transportLocationId) return 0;
            const locRef = doc(db, "locations", transportLocationId);
            const locSnap = await getDoc(locRef);
            if (!locSnap.exists()) return 0;
            const locationType = locSnap.data().locationType;
            const q = query(collection(db, "location_types"),where("name", "==", locationType),where("schoolId", "==", schoolId));
            const typeSnap = await getDocs(q);
            if (!typeSnap.empty) {
                return typeSnap.docs[0].data().fee || 0;
            }
            return 0;

        } catch (err) {
            console.error("Transport Fee Error:", err);
            return 0;
        }
    };

    const resolveHeadAmount = async (headId, student) => {
        // Transport Head Dynamic
        if (headId === "9da5fe5b-606e-49d1-89cc-173698c38dbf") {
            if (student) {
                return await fetchTransportFee(student.id);
            }
            return 0;
        }

        // Default from master
        const master = feeHeadMaster.find(h => h.id === headId);
        return master?.defaultAmount || 0;
    };

    function TransportLocationName({ locationId }) {

        const [locationName, setLocationName] = React.useState("");

        useEffect(() => {

            const fetchLocation = async () => {

                if (!locationId) return;

                try {

                    const snap = await getDoc(doc(db, "locations", locationId));

                    if (snap.exists()) {

                        const data = snap.data();

                        setLocationName(
                            data.locationName ||
                            data.name ||
                            data.locationType ||
                            ""
                        );
                    }

                } catch (err) {
                    console.log(err);
                }
            };

            fetchLocation();

        }, [locationId]);

        if (!locationName) return null;

        return (
            <Typography
                variant="caption"
                color="primary"
                sx={{
                    fontWeight: 600,
                    ml: 0.5
                }}
            >
                {locationName}
            </Typography>
        );
    }

    return (
        <Box p={1} bgcolor="#f4f7f9" minHeight="100vh">
            {/* Header Section */}
            <Paper elevation={0} sx={{ p: 1, mb: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography variant="h5" color="textSecondary" sx={{ letterSpacing: 1.5, fontWeight: 700 }}>
                            Fee Structure Setup
                        </Typography>
                    </Box>
                    <ToggleButtonGroup value={mode} exclusive size="small" onChange={(e, v) => v && setMode(v)} color="primary">
                        <ToggleButton value="single"><Person sx={{ mr: 1 }} /> Individual</ToggleButton>
                        <ToggleButton value="bulk"><School sx={{ mr: 1 }} /> Bulk Class</ToggleButton>
                    </ToggleButtonGroup>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Grid className="row" spacing={2}>
                    <Grid className="col-sm-6" item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ mb: 2 }} gutterBottom>1. Student/Class Selection</Typography>
                        {mode === 'single' ? (
                            <Autocomplete
                                options={students}
                                getOptionLabel={(opt) => `${opt.fullName} (${opt.className})`}
                                onChange={(e, val) => handleSelectionChange(val, 'student')}
                                renderInput={(params) => <TextField {...params} label="Search Student Name" size="small" fullWidth />}
                            />
                        ) : (
                            <Stack direction="row" spacing={2}>
                                {/* Class */}
                                <FormControl fullWidth size="small">
                                    <InputLabel>Select Class</InputLabel>
                                    <Select
                                        value={selectedClass}
                                        label="Select Class"
                                        onChange={(e) => handleSelectionChange(e.target.value, 'class')}
                                    >
                                        {courses.map(c => (
                                            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {/* NEW: Student Type Filter */}
                                <FormControl fullWidth size="small">
                                    <InputLabel>Type</InputLabel>
                                    <Select
                                        value={selectedType}
                                        label="Type"
                                        onChange={(e) => setSelectedType(e.target.value)}
                                    >
                                        <MenuItem value="">All</MenuItem>
                                        <MenuItem value="Day School">Day School</MenuItem>
                                        <MenuItem value="Boarding">Boarding</MenuItem>
                                    </Select>
                                </FormControl>

                                {/* Students Multi Select */}
                                <FormControl fullWidth size="small">
                                    <Select
                                        multiple
                                        value={selectedStudentIds}
                                        onChange={(e) => setSelectedStudentIds(e.target.value)}
                                        renderValue={(selected) => (
                                            <Chip label={`${selectedType} Selected`} size="small" color="primary" />
                                        )}
                                    >
                                        {studentsInClass.map((s) => (
                                            <MenuItem key={s.id} value={s.id}>
                                                <Checkbox checked={selectedStudentIds.indexOf(s.id) > -1} />
                                                <ListItemText primary={s.fullName} secondary={s.grno} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Stack>
                        )}
                    </Grid>
                    {selectedStudent && (
                        <Grid className="col-sm-6" item xs={12} md={6}>
                            <Box sx={{
                                p: 1.5,
                                bgcolor: '#e3f2fd',
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2 // Adds space between photo and text
                            }}>
                                {/* Student Photo Section */}
                                <Avatar
                                    src={selectedStudent.studentPhoto}
                                    alt={selectedStudent.fullName}
                                    sx={{
                                        width: 65,
                                        height: 65,
                                        border: '2px solid #fff',
                                        boxShadow: '0px 2px 4px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    {/* Fallback to first letter if photoUrl is empty */}
                                    {selectedStudent.fullName?.charAt(0)}
                                </Avatar>

                                <Box sx={{ flex: 1 }}>

                                    <Typography variant="body1" fontWeight="bold">
                                        {selectedStudent.fullName}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Class: {selectedStudent.className} | Address: {selectedStudent.address || 'N/A'} | Student Type: {selectedStudent.studenttype || 'N/A'}
                                    </Typography>
                                    <Typography variant="caption" display="block" color="primary" sx={{ fontWeight: 600 }}>
                                        {selectedStudent.father_mobile1}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Paper>

            <Grid className="row" spacing={3}>
                <Grid className="col-sm-6" item xs={12} lg={5}>
                    <Card
                        elevation={0}
                        sx={{
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                            overflow: 'hidden'
                        }}
                    >
                        <Box
                            sx={{
                                background: 'linear-gradient(90deg, #1e3a8a 0%, #3b82f6 100%)',
                                p: 1,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}
                        >
                            <Typography variant="h6" color="white" fontWeight="600">
                                Fee Structure Master
                            </Typography>
                            <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', px: 1.5, py: 0.5, borderRadius: 2 }}>
                                <Typography variant="caption" color="white" fontWeight="bold">
                                    CONFIG MODE
                                </Typography>
                            </Box>
                        </Box>

                        <Box p={1}>
                            {/* Table Section */}
                            <TableContainer sx={{ maxHeight: 400, mb: 3, borderRadius: 2, border: '1px solid #f0f0f0' }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', color: '#64748b' }}>Particulars</TableCell>
                                            <TableCell align="right" sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', color: '#64748b' }}>Schedule / Date</TableCell>
                                            <TableCell align="right" sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', color: '#64748b', width: 120 }}>Amount</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {feeHeads.map((h) => {
                                            if (!h.checked) return null;

                                            const isInstallment = h.installment === "Yes";
                                            const installments = feeHeadInstallments[h.headId] || [];

                                            return (
                                                <React.Fragment key={h.headId}>
                                                    {/* Parent Row */}
                                                    <TableRow sx={{ bgcolor: isInstallment ? '#f1f5f9' : 'inherit' }}>
                                                        <TableCell sx={{ fontWeight: '600', color: '#1e293b' }}>
                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                <Stack spacing={0.3}>
                                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                                        <Typography fontWeight="600">
                                                                            {h.name}
                                                                        </Typography>

                                                                        {isInstallment && (
                                                                            <Button
                                                                                size="small"
                                                                                variant="text"
                                                                                sx={{ fontSize: '0.65rem', py: 0 }}
                                                                                onClick={() => addInstallmentRow(h.headId)}
                                                                            >
                                                                                + Add Split
                                                                            </Button>
                                                                        )}
                                                                    </Stack>

                                                                    {h.headId === "9da5fe5b-606e-49d1-89cc-173698c38dbf" && (
                                                                        <TransportLocationName
                                                                            locationId={selectedStudent.transport}
                                                                        />
                                                                    )}
                                                                </Stack>

                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {!isInstallment && <Typography variant="caption" color="textSecondary">One-time</Typography>}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                                                <TextField
                                                                    size="small"
                                                                    type="number"
                                                                    variant="standard"
                                                                    value={h.amount}
                                                                    onChange={(e) => {
                                                                        const updated = feeHeads.map(x =>
                                                                            x.headId === h.headId ? { ...x, amount: parseFloat(e.target.value) || 0 } : x
                                                                        );
                                                                        setFeeHeads(updated);
                                                                    }}
                                                                    InputProps={{
                                                                        startAdornment: <Typography variant="caption" sx={{ mr: 0.5, color: 'text.secondary' }}>₹</Typography>,
                                                                        disableUnderline: false // Keeps the standard look
                                                                    }}
                                                                    sx={{ width: '80px' }} // Controls the width so it doesn't push the button off-screen
                                                                />

                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => setFeeHeads(prev => prev.filter(x => x.headId !== h.headId))}
                                                                    sx={{
                                                                        bgcolor: '#fff1f0',
                                                                        '&:hover': { bgcolor: '#ffccc7' },
                                                                        transition: '0.2s'
                                                                    }}
                                                                >
                                                                    <Delete fontSize="inherit" />
                                                                </IconButton>
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>

                                                    {/* Installment Sub-Rows */}
                                                    {isInstallment && installments.map((inst, i) => (
                                                        <TableRow key={`${h.headId}-inst-${i}`} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                                            <TableCell sx={{ pl: 4 }}>
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    variant="standard"
                                                                    placeholder="Installment Name"
                                                                    value={inst.name}
                                                                    onChange={(e) => handleHeadInstallmentChange(h.headId, i, "name", e.target.value)}
                                                                    sx={{ '& .MuiInput-root': { fontSize: '0.875rem' } }}
                                                                />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <TextField
                                                                    type="date"
                                                                    size="small"
                                                                    variant="standard"
                                                                    value={inst.dueDate || ""}
                                                                    onChange={(e) => handleHeadInstallmentChange(h.headId, i, "dueDate", e.target.value)}
                                                                    sx={{ '& .MuiInput-root': { fontSize: '0.75rem' } }}
                                                                />
                                                            </TableCell>
                                                            {/* <TableCell align="right">
                                                                <TextField
                                                                    type="number"
                                                                    size="small"
                                                                    variant="standard"
                                                                    value={inst.amount}
                                                                    onChange={(e) => handleHeadInstallmentChange(h.headId, i, "amount", e.target.value)}
                                                                    InputProps={{ startAdornment: <Typography variant="caption" sx={{ mr: 0.5 }}>₹</Typography> }}
                                                                />
                                                            </TableCell> */}
                                                            <TableCell align="right">
                                                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                                                    <TextField
                                                                        type="number"
                                                                        size="small"
                                                                        variant="standard"
                                                                        value={inst.amount}
                                                                        onChange={(e) => handleHeadInstallmentChange(h.headId, i, "amount", e.target.value)}
                                                                        InputProps={{
                                                                            startAdornment: <Typography variant="caption" sx={{ mr: 0.5 }}>₹</Typography>
                                                                        }}
                                                                    />

                                                                    {/* ✅ DELETE BUTTON */}
                                                                    <IconButton
                                                                        size="small"
                                                                        color="error"
                                                                        onClick={() => removeInstallmentRow(h.headId, i)}
                                                                        sx={{
                                                                            bgcolor: '#fff1f0',
                                                                            '&:hover': { bgcolor: '#ffccc7' }
                                                                        }}
                                                                    >
                                                                        <Delete fontSize="small" />
                                                                    </IconButton>
                                                                </Stack>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </React.Fragment>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Quick Add Fee Head */}
                            <Stack direction="row" spacing={1} p={1.5} sx={{ bgcolor: '#f8fafc', borderRadius: 3, border: '1px dashed #cbd5e1' }}>
                                <FormControl size="small" fullWidth>
                                    <Select
                                        displayEmpty
                                        value={customHead.headId}
                                        onChange={async (e) => {
                                            const selected = feeHeadMaster.find(h => h.id === e.target.value);

                                            if (selected) {
                                                let amount = await resolveHeadAmount(selected.id, selectedStudent);

                                                setCustomHead({
                                                    headId: selected.id,
                                                    name: selected.fee_heads,
                                                    amount: amount
                                                });
                                            }
                                        }}
                                        renderValue={(selected) => {
                                            if (!selected) return <Typography color="textSecondary" variant="body2">Select Fee Head...</Typography>;
                                            return feeHeadMaster.find(h => h.id === selected)?.fee_heads;
                                        }}
                                    >
                                        {feeHeadMaster.map((h) => <MenuItem key={h.id} value={h.id}>{h.fee_heads}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <TextField
                                    size="small"
                                    type="number"
                                    placeholder="Amt"
                                    sx={{ width: 120, bgcolor: 'white' }}
                                    value={customHead.amount}
                                    onChange={e => setCustomHead({ ...customHead, amount: e.target.value })}
                                />
                                <Button variant="contained" onClick={addCustomHead} sx={{ minWidth: 48, borderRadius: 2 }}>
                                    <Add fontSize="small" />
                                </Button>
                            </Stack>

                            {/* Adjustments Section */}

                        </Box>
                    </Card>
                </Grid>
                {/* Right Column: Payment Schedule (Matches the Paper Form) */}
                <Paper className="col-sm-6" item xs={12} lg={7}>
                    <Box mt={2}>
                        <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Settings fontSize="inherit" /> Adjustments & Discounts
                        </Typography>
                        <Stack spacing={1.5}>
                            {appliedDiscounts.map((row, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        gap: 1,
                                        p: 1.5,
                                        bgcolor: '#fff9f9',
                                        borderRadius: 2,
                                        border: '1px solid #fee2e2'
                                    }}
                                >
                                    <Select
                                        size="small"
                                        fullWidth
                                        variant="outlined"
                                        sx={{ bgcolor: 'white' }}
                                        value={row.typeId}
                                        onChange={(e) => {
                                            const copy = [...appliedDiscounts];
                                            copy[index].typeId = e.target.value;
                                            setAppliedDiscounts(copy);
                                        }}
                                    >
                                        {discountTypes.map(t => <MenuItem key={t.id} value={t.id}>{t.discountName}</MenuItem>)}
                                    </Select>
                                    <TextField
                                        size="small"
                                        type="number"
                                        label="Reduction"
                                        sx={{ width: 140, bgcolor: 'white' }}
                                        value={row.amount}
                                        onChange={(e) => {
                                            const copy = [...appliedDiscounts];
                                            copy[index].amount = parseFloat(e.target.value) || 0;
                                            setAppliedDiscounts(copy);
                                        }}
                                    />
                                    <IconButton size="small" color="error" onClick={() => setAppliedDiscounts(appliedDiscounts.filter((_, i) => i !== index))}>
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                            <Button
                                fullWidth
                                startIcon={<Add />}
                                variant="outlined"
                                color="inherit"
                                onClick={() => setAppliedDiscounts([...appliedDiscounts, { typeId: "", amount: 0 }])}
                                sx={{ borderStyle: 'dashed', py: 1, borderRadius: 2, color: '#64748b' }}
                            >
                                Add Discount/Deduction
                            </Button>
                        </Stack>
                    </Box>
                    <Card variant="outlined" sx={{ borderRadius: 3, position: 'relative', marginTop: 2 }}>
                        <Box sx={{ p: 1, bgcolor: '#374151', color: 'white', display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle1" fontWeight="bold">Academic Session Fee Details</Typography>
                        </Box>

                        <Box p={0}>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>PARTICULARS</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>DUE DATE</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>AMOUNT PAYABLE</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {/* Summary Row */}
                                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                            <TableCell colSpan={2} align="right">
                                                <Typography variant="h6">Total Payable:</Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="h6" color="primary.main" fontWeight="900">
                                                    ₹{totalAmount.toLocaleString()}/-
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>

                        <Box p={3}>
                            <Button
                                fullWidth variant="contained" size="large"
                                sx={{ py: 2, borderRadius: 2, fontWeight: 'bold', boxShadow: 3 }}
                                startIcon={<Save />}
                                onClick={handleBulkSave}
                            >
                                Confirm Payment Structure {mode === 'bulk' ? `for ${selectedStudentIds.length} Students` : ''}
                            </Button>
                        </Box>
                    </Card>
                </Paper>
            </Grid>
        </Box>
    );
}

export default FeeStructureSetup;